import React, { useState } from "react";
import SEOHead from "../../components/SEO/SEOHead";
import LegalPageLayout from "../../components/Legal/LegalPageLayout";

const AccessibilityStatement = () => {
  const [activeSection, setActiveSection] = useState("commitment");

  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const sections = [
    { id: "commitment", title: "Our Commitment" },
    { id: "conformance", title: "Conformance Status" },
    { id: "features", title: "Accessibility Features" },
    { id: "browser-support", title: "Browser & Assistive Tech" },
    { id: "technical", title: "Technical Specifications" },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <SEOHead
        title="Accessibility Statement - Bowling Tournament App"
        description="Learn about our commitment to digital accessibility, WCAG compliance, accessibility features, and how to report accessibility issues."
        keywords={[
          "accessibility",
          "WCAG",
          "ADA compliance",
          "screen reader",
          "keyboard navigation",
          "digital accessibility",
        ]}
        canonicalUrl="/accessibility-statement"
        noIndex={false}
        additionalMeta={[{ name: "last-modified", content: currentDate }]}
      />
      <LegalPageLayout
        title="Accessibility Statement"
        lastUpdated={currentDate}
        sections={sections}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
      >
        <div className="space-y-12">
                {/* Our Commitment Section */}
                <section id="commitment">
                  <h2 className="heading-display text-xl mb-4">
                    Our Commitment
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      Bowling Tournament App is committed to ensuring digital
                      accessibility for people with disabilities. We are
                      continually improving the user experience for everyone and
                      applying the relevant accessibility standards.
                    </p>
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Our Goal:</strong> We strive to provide an
                        inclusive experience that works for all users, regardless
                        of their abilities or the technologies they use to access
                        our platform.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Conformance Status Section */}
                <section id="conformance">
                  <h2 className="heading-display text-xl mb-4">
                    Conformance Status
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      The Web Content Accessibility Guidelines (WCAG) defines
                      requirements for designers and developers to improve
                      accessibility for people with disabilities. We aim to
                      conform to WCAG 2.1 Level AA standards.
                    </p>
                    <p className="mb-4">
                      While we strive to adhere to these guidelines, we
                      acknowledge that some areas of the website may not yet be
                      fully accessible. We are working to improve these areas.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Current Status
                    </h3>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        Most pages and features are accessible to screen readers
                      </li>
                      <li>Navigation is keyboard accessible</li>
                      <li>Color contrast meets WCAG AA standards</li>
                      <li>Forms include proper labels and error messages</li>
                      <li>Images include alternative text descriptions</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Areas Under Improvement
                    </h3>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        Enhanced keyboard navigation for complex interactions
                      </li>
                      <li>Improved focus indicators for better visibility</li>
                      <li>Additional ARIA labels for dynamic content</li>
                      <li>Video content with captions and transcripts</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Note:</strong> We regularly audit our platform
                        for accessibility compliance and implement improvements
                        based on user feedback and industry best practices.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Accessibility Features Section */}
                <section id="features">
                  <h2 className="heading-display text-xl mb-4">
                    Accessibility Features
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Keyboard Navigation
                    </h3>
                    <p className="mb-4">
                      Our platform supports full keyboard navigation:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>Tab key to navigate through interactive elements</li>
                      <li>Enter and Space keys to activate buttons and links</li>
                      <li>Arrow keys for menu navigation and form controls</li>
                      <li>Escape key to close modals and dropdowns</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Screen Reader Support
                    </h3>
                    <p className="mb-4">
                      We provide comprehensive screen reader support:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        Semantic HTML structure for proper content hierarchy
                      </li>
                      <li>
                        ARIA labels and descriptions for complex interactions
                      </li>
                      <li>Alternative text for all meaningful images</li>
                      <li>Live regions for dynamic content updates</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Visual Accessibility
                    </h3>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        High contrast color schemes that meet WCAG standards
                      </li>
                      <li>Scalable text that works with browser zoom up to 200%</li>
                      <li>Clear focus indicators for keyboard navigation</li>
                      <li>Consistent visual hierarchy and layout patterns</li>
                    </ul>
                  </div>
                </section>

                {/* Browser and Assistive Technology Support Section */}
                <section id="browser-support">
                  <h2 className="heading-display text-xl mb-4">
                    Browser and Assistive Technology Support
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      Our platform is designed to work with a wide range of
                      browsers and assistive technologies:
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Supported Browsers
                    </h3>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>Chrome (latest version)</li>
                      <li>Firefox (latest version)</li>
                      <li>Safari (latest version)</li>
                      <li>Edge (latest version)</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Assistive Technologies
                    </h3>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        Screen readers (NVDA, JAWS, VoiceOver, TalkBack)
                      </li>
                      <li>Voice control software</li>
                      <li>Switch navigation devices</li>
                      <li>Magnification software</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Recommendation:</strong> For the best experience,
                        please use the latest version of your preferred browser
                        and keep your assistive technology software updated.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Technical Specifications Section */}
                <section id="technical">
                  <h2 className="heading-display text-xl mb-4">
                    Technical Specifications
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      Accessibility of Bowling Tournament App relies on the
                      following technologies to work with the particular
                      combination of web browser and any assistive technologies
                      or plugins installed on your computer:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>HTML5 for semantic structure</li>
                      <li>WAI-ARIA for enhanced accessibility</li>
                      <li>CSS3 for styling and layout</li>
                      <li>JavaScript for interactive functionality</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Standards Compliance
                    </h3>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>WCAG 2.1 Level AA guidelines</li>
                      <li>Section 508 compliance (where applicable)</li>
                      <li>ADA compliance standards</li>
                      <li>EN 301 549 accessibility requirements</li>
                    </ul>
                  </div>
                </section>

                {/* Contact & Updates Section */}
                <section className="border-t border-brand-charcoal/10 pt-8">
                  <h2 className="heading-display text-xl mb-4">
                    Contact & Updates
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Questions About Accessibility?
                    </h3>
                    <p className="mb-4">
                      If you have questions about accessibility or need to report
                      an issue, please contact us:
                    </p>
                    <p className="font-medium">
                      Email: accessibility@bowlingtournamentapp.com
                    </p>
                    <p className="text-sm text-brand-muted mb-4">
                      We typically respond within 24-48 hours
                    </p>
                    <p className="mb-4">
                      When reporting accessibility issues, please include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        The specific page or feature where you encountered the issue
                      </li>
                      <li>
                        A description of the accessibility barrier you experienced
                      </li>
                      <li>
                        The assistive technology you were using
                      </li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Statement Updates
                    </h3>
                    <p>
                      This accessibility statement is reviewed and updated
                      regularly to reflect our current accessibility status and
                      improvements.
                    </p>
                    <p className="font-medium mt-4">
                      Last Updated: {currentDate}
                    </p>
                    <p className="text-sm text-brand-muted">
                      Significant changes will be communicated via email to
                      registered users
                    </p>
                  </div>
                </section>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default AccessibilityStatement;
